import { db } from '../data/db';
import type { LatLng } from '../data/types';
import { isOnline } from '../sync/connectivity';

export const TOWNSVILLE: LatLng = [-19.2589, 146.8169];

export interface GeocodeResult { displayName: string; location: LatLng }

// Nominatim's usage policy allows at most one request per second.
let lastRequest = 0;
async function throttle() {
  const wait = lastRequest + 1100 - Date.now();
  lastRequest = Math.max(Date.now(), lastRequest + 1100);
  if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
}

export async function searchAddress(query: string, limit = 1): Promise<GeocodeResult[]> {
  await throttle();
  const response = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=${limit}&countrycodes=au&q=${encodeURIComponent(query)}`);
  if (!response.ok) throw new Error('Search unavailable');
  const results = await response.json() as Array<{ display_name: string; lat: string; lon: string }>;
  return results.map((result) => ({ displayName: result.display_name, location: [Number(result.lat), Number(result.lon)] }));
}

/**
 * Coordinates for an address, cached on this device so maps and heat layers work offline after the first
 * lookup. Returns null when the address cannot be found or the device is offline with no cached result.
 */
export async function locateAddress(address: string): Promise<LatLng | null> {
  const key = address.trim().toLowerCase();
  if (!key) return null;
  const cached = await db.geocodes.get(key);
  if (cached && (cached.location || Date.now() - cached.at < 7 * 86400000)) return cached.location;
  if (!isOnline()) return cached?.location ?? null;
  try {
    const [result] = await searchAddress(address);
    const location = result?.location ?? null;
    await db.geocodes.put({ address: key, location, at: Date.now() });
    return location;
  } catch {
    return cached?.location ?? null;
  }
}

export function currentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('Location is not available on this device.'));
    navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 });
  });
}

export function locationErrorMessage(error: unknown): string {
  const code = (error as GeolocationPositionError | undefined)?.code;
  if (code === 1) return 'Location permission was denied. Enable location access for this site and try again.';
  if (code === 2) return 'Your location could not be determined. Check GPS/location services and try again.';
  if (code === 3) return 'Location lookup timed out. Try again once the device has a clearer signal.';
  return (error as Error)?.message || 'Location is unavailable on this device.';
}
